import React, { useEffect, useState } from 'react';
import api from '../config/axios';
import { useAuth } from '../context/AuthContext';
import { FaChartBar, FaTable } from 'react-icons/fa';

interface Period {
  year: number;
  month: number;
}

interface ReportRow {
  sectorOperativo: string;
  cuadrillaCodigo: string;
  cuadrillaNombre: string;
  totalOrdenes: number;
}

interface MonthlyReportResponse {
  periods: Period[];
  selectedPeriod: Period | null;
  rows: ReportRow[];
  total: number;
}

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const Reports: React.FC = () => {
  const { user } = useAuth();
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  const [selectedMonth, setSelectedMonth] = useState<number | ''>('');
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReport = async (year?: number, month?: number) => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (year) params.year = year;
      if (month) params.month = month;

      const response = await api.get<MonthlyReportResponse>('/winbo/reports/monthly', { params });
      const data = response.data;

      setPeriods(data.periods || []);
      setRows(data.rows || []);
      setTotal(data.total || 0);

      if (data.selectedPeriod) {
        setSelectedYear(data.selectedPeriod.year);
        setSelectedMonth(data.selectedPeriod.month);
      } else if (data.periods && data.periods.length > 0) {
        setSelectedYear(data.periods[0].year);
        setSelectedMonth(data.periods[0].month);
      }
    } catch (err: any) {
      console.error('Error cargando reporte mensual:', err);
      const msg = err.response?.data?.message || 'Error al cargar el reporte mensual';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = e.target.value ? parseInt(e.target.value, 10) : '';
    setSelectedYear(year);
    if (typeof year === 'number' && typeof selectedMonth === 'number') {
      loadReport(year, selectedMonth);
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const month = e.target.value ? parseInt(e.target.value, 10) : '';
    setSelectedMonth(month);
    if (typeof month === 'number' && typeof selectedYear === 'number') {
      loadReport(selectedYear, month);
    }
  };

  const handleRefresh = () => {
    if (typeof selectedYear === 'number' && typeof selectedMonth === 'number') {
      loadReport(selectedYear, selectedMonth);
    } else {
      loadReport();
    }
  };

  const availableYears = Array.from(new Set(periods.map(p => p.year))).sort((a, b) => b - a);
  const availableMonths = periods
    .filter(p => (typeof selectedYear === 'number' ? p.year === selectedYear : true))
    .map(p => p.month);
  const uniqueMonths = Array.from(new Set(availableMonths)).sort((a, b) => a - b);

  if (user?.role !== 'administrador' && user?.role !== 'supervisor' && user?.role !== 'backoffice') {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning" role="alert">
          No tienes permisos para acceder a esta sección.
        </div>
      </div>
    );
  }

  const maxValue = rows.length > 0 ? Math.max(...rows.map(r => r.totalOrdenes)) : 0;

  // Agregar acumulado por sector operativo para gráfico separado
  const sectorTotals = rows.reduce<Record<string, number>>((acc, row) => {
    const sector = row.sectorOperativo || 'Sin sector';
    acc[sector] = (acc[sector] || 0) + row.totalOrdenes;
    return acc;
  }, {});

  const sectorRows = Object.entries(sectorTotals)
    .map(([sector, total]) => ({ sector, total }))
    .sort((a, b) => b.total - a.total);

  const maxSectorValue = sectorRows.length > 0 ? Math.max(...sectorRows.map(r => r.total)) : 0;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h2 className="mb-0 d-flex align-items-center gap-2">
            <FaChartBar />
            Reportes de WinBO
          </h2>
          <p className="text-muted mb-0 small">
            Órdenes de tipo <strong>INSTALACION</strong> en estado <strong>Finalizada</strong>, agrupadas por cuadrilla.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title mb-3">Filtros</h5>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3 col-lg-2">
              <label className="form-label small">Año</label>
              <select
                className="form-select form-select-sm"
                value={selectedYear || ''}
                onChange={handleYearChange}
                disabled={loading || availableYears.length === 0}
              >
                {availableYears.length === 0 && <option value="">Sin datos</option>}
                {availableYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4 col-lg-3 d-flex align-items-end gap-2">
              <div className="flex-grow-1">
                <label className="form-label small">Mes</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedMonth || ''}
                  onChange={handleMonthChange}
                  disabled={loading || uniqueMonths.length === 0}
                >
                  {uniqueMonths.length === 0 && <option value="">Sin datos</option>}
                  {uniqueMonths.map(month => (
                    <option key={month} value={month}>
                      {monthNames[month - 1]} {selectedYear || ''}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={handleRefresh}
                disabled={loading}
                style={{ whiteSpace: 'nowrap' }}
              >
                Actualizar
              </button>
            </div>

            <div className="col-12 col-md-5 col-lg-4">
              <div className="text-muted small">
                Mostrando órdenes de instalación finalizadas para el período seleccionado.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-lg-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title d-flex align-items-center gap-2 mb-3">
                <FaChartBar />
                Gráfico por cuadrilla
              </h5>
              {loading ? (
                <p className="text-muted">Cargando...</p>
              ) : rows.length === 0 ? (
                <p className="text-muted">No hay datos para el período seleccionado.</p>
              ) : (
                <div>
                  {rows.map(row => (
                    <div key={row.cuadrillaCodigo} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="small">
                          <strong>{row.cuadrillaCodigo}</strong> - {row.cuadrillaNombre}
                        </span>
                        <span className="badge bg-primary">{row.totalOrdenes}</span>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{
                            width: maxValue > 0 ? `${(row.totalOrdenes / maxValue) * 100}%` : '0%'
                          }}
                          aria-valuenow={row.totalOrdenes}
                          aria-valuemin={0}
                          aria-valuemax={maxValue}
                        ></div>
                      </div>
                    </div>
                  ))}
                  <hr />
                  <div className="d-flex justify-content-between small">
                    <span>Total de órdenes</span>
                    <strong>{total}</strong>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title d-flex align-items-center gap-2 mb-3">
                <FaTable />
                Detalle por cuadrilla
              </h5>
              {loading ? (
                <p className="text-muted">Cargando...</p>
              ) : rows.length === 0 ? (
                <p className="text-muted">No hay datos para el período seleccionado.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Cuadrilla</th>
                        <th>Nombre</th>
                        <th className="text-end">Órdenes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => (
                        <tr key={row.cuadrillaCodigo}>
                          <td><strong>{row.cuadrillaCodigo}</strong></td>
                          <td>{row.cuadrillaNombre}</td>
                          <td className="text-end">{row.totalOrdenes}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan={2} className="text-end">Total</th>
                        <th className="text-end">{total}</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico separado por sector operativo */}
      <div className="row g-4 mt-1">
        <div className="col-12">
          <div className="card h-100">
            <div className="card-body">
              <h5 className="card-title d-flex align-items-center gap-2 mb-3">
                <FaChartBar />
                Gráfico por sector operativo (acumulado)
              </h5>
              {loading ? (
                <p className="text-muted">Cargando...</p>
              ) : sectorRows.length === 0 ? (
                <p className="text-muted">No hay datos para el período seleccionado.</p>
              ) : (
                <div>
                  {sectorRows.map(row => (
                    <div key={row.sector} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="small">
                          <strong>{row.sector}</strong>
                        </span>
                        <span className="badge bg-success">{row.total}</span>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div
                          className="progress-bar bg-success"
                          role="progressbar"
                          style={{
                            width: maxSectorValue > 0 ? `${(row.total / maxSectorValue) * 100}%` : '0%'
                          }}
                          aria-valuenow={row.total}
                          aria-valuemin={0}
                          aria-valuemax={maxSectorValue}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;


